import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FiX,
  FiCalendar,
  FiImage,
  FiFileText,
  FiDownload,
  FiLink,
  FiTrash2,
  FiLoader,
} from 'react-icons/fi';

interface TaskDetailsModalProps {
  task: any | null;
  onClose: () => void;
  onOpenEdit: (task: any) => void;
  onDelete: (taskId: string) => void;
  isDeleting: boolean;
  isGuestUser: boolean;
}

export const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  task,
  onClose,
  onOpenEdit,
  onDelete,
  isDeleting,
  isGuestUser,
}) => {
  return (
    <AnimatePresence>
      {task && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-[#0B0B0B] border border-white/10 rounded-[28px] p-5 sm:p-6 text-left flex flex-col max-h-[82vh] z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)] overflow-hidden"
          >
            {/* Sticky Header */}
            <div className="shrink-0 flex items-center justify-between pb-4 mb-4 border-b border-white/5 bg-[#0B0B0B] z-20">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-white">Task Details</h2>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
                    task.alerts?.includes('Overdue')
                      ? 'bg-rose-950/60 text-rose-400 border-rose-800/50'
                      : task.status === 'Complete'
                        ? 'bg-emerald-950/60 text-emerald-400 border-emerald-800/50'
                        : task.status === 'In Progress'
                          ? 'bg-blue-950/60 text-blue-400 border-blue-800/50'
                          : 'bg-zinc-800 text-zinc-300 border-white/10'
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      task.alerts?.includes('Overdue')
                        ? 'bg-rose-400'
                        : task.status === 'Complete'
                          ? 'bg-emerald-400'
                          : task.status === 'In Progress'
                            ? 'bg-blue-400'
                            : 'bg-zinc-400'
                    }`}
                  />
                  {task.alerts?.includes('Overdue') ? 'Overdue' : task.status}
                </span>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto no-scrollbar space-y-4 pr-1">
              {/* Title Section */}
              <div>
                <p className="text-zinc-500 text-xs font-medium mb-1">Task Title</p>
                <h3 className="text-xl font-bold text-white leading-snug">{task.title}</h3>
              </div>

              {/* Metadata Card */}
              <div className="bg-[#121212] border border-white/5 rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider mb-1.5">
                    Priority
                  </p>
                  <span
                    className={`inline-flex items-center text-xs font-extrabold uppercase px-2.5 py-1 rounded-full ${
                      task.priority === 'High'
                        ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                        : task.priority === 'Medium'
                          ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                          : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                    }`}
                  >
                    {task.priority}
                  </span>
                </div>

                <div>
                  <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider mb-1.5">
                    Due Date
                  </p>
                  <div className="flex items-center gap-1.5 text-zinc-200 text-xs font-semibold">
                    <FiCalendar className="w-3.5 h-3.5 text-zinc-400" />
                    <span>
                      {task.dueDate && task.dueDate !== 'No Date' && task.dueDate !== 'No due date'
                        ? task.dueDate
                        : 'No due date'}
                    </span>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider mb-1.5">
                    Assignee
                  </p>
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-6 h-6 rounded-full ${task.assignee.color} text-[10px] font-extrabold text-white flex items-center justify-center`}
                    >
                      {task.assignee.initials}
                    </span>
                    <span className="text-zinc-200 text-xs font-semibold truncate max-w-[100px]">
                      {task.assignee.name}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description Box */}
              {task.description && (
                <div>
                  <p className="text-zinc-400 text-xs font-semibold mb-2">Description</p>
                  <div className="bg-[#121212] border border-white/5 rounded-2xl p-4 text-zinc-300 text-xs font-medium leading-relaxed whitespace-pre-wrap">
                    {task.description}
                  </div>
                </div>
              )}

              {/* Cover Image */}
              {task.coverImage && (
                <div>
                  <p className="text-zinc-400 text-xs font-semibold mb-2">Cover Image</p>
                  <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#121212]">
                    <img
                      src={task.coverImage}
                      alt="Task Cover"
                      className="w-full max-h-64 object-cover"
                    />
                  </div>
                </div>
              )}

              {/* Attachments */}
              {task.attachments && task.attachments.length > 0 && (
                <div>
                  <p className="text-zinc-400 text-xs font-semibold mb-2">Attachments</p>
                  <div className="space-y-2">
                    {task.attachments.map((att: string, idx: number) => {
                      const fileName = att.split('/').pop() || `Attachment ${idx + 1}`;
                      const isImg = /\.(jpg|jpeg|png|webp|gif)$/i.test(att);
                      return (
                        <a
                          key={idx}
                          href={att}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-between bg-[#121212] border border-white/5 hover:border-white/10 rounded-xl p-3 text-xs font-semibold text-zinc-300 transition-all group"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            {isImg ? (
                              <FiImage className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                            ) : (
                              <FiFileText className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                            )}
                            <span className="truncate max-w-[220px]">{fileName}</span>
                          </div>
                          <FiDownload className="w-3.5 h-3.5 text-blue-400 group-hover:text-blue-300 shrink-0" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* External Links */}
              {task.externalLink && (
                <div>
                  <p className="text-zinc-400 text-xs font-semibold mb-2">External Links</p>
                  <a
                    href={task.externalLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-xs font-semibold transition-colors truncate max-w-full bg-[#121212] border border-white/5 rounded-xl px-3.5 py-2"
                  >
                    <FiLink className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{task.externalLink}</span>
                  </a>
                </div>
              )}
            </div>

            {/* Sticky Footer */}
            {!isGuestUser && (
              <div className="shrink-0 flex items-center justify-end gap-3 pt-4 mt-4 border-t border-white/5 bg-[#0B0B0B] z-20">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenEdit(task);
                  }}
                  className="px-5 py-2.5 rounded-full border border-white/10 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold transition-all cursor-pointer"
                >
                  Edit Task
                </button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => onDelete(task.id)}
                  className="px-5 py-2.5 rounded-full bg-rose-500 hover:bg-rose-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                >
                  {isDeleting ? (
                    <>
                      <FiLoader className="w-3.5 h-3.5 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <FiTrash2 className="w-3.5 h-3.5" />
                      <span>Delete Task</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
